// Fixed-step simulation: player platforming, run recording, echo replay,
// buttons, doors, spikes, win detection.
//
// The core trick: every tick of an attempt we record the player's position.
// Pressing [R] turns that tape into an Echo and restarts the attempt at t=0.
// All echoes replay their tapes from t=0 in lockstep with the new attempt,
// then FREEZE on their final frame — so an echo that ended standing on a
// button keeps that button held forever (until the next reset re-syncs all).

import type { Frame, Echo, ParsedLevel, FxEvent, DoorDef } from './types';
import { TILE, TICK, MAX_TAPE } from './types';

export const PLAYER_W = 20;
export const PLAYER_H = 27;

const MOVE_SPEED = 230;
const GROUND_ACCEL = 2800;
const AIR_ACCEL = 2000;
const GRAVITY = 1800;
const JUMP_VEL = 700;
const MAX_FALL = 920;
const COYOTE = 0.09;
const JUMP_BUFFER = 0.12;

// Spring pad launch — much taller than a jump (~136px) or echo-stool, so a
// spring opens vertical routes nothing else can reach. 1080 ⇒ ~324px (~10.8 tiles).
const SPRING_VEL = 1080;
// Crumble slab: ticks a body must stand on it before it gives way, then how long
// it stays gone before respawning. Generous enough that a steady walk clears it;
// only hesitation drops you.
const CRUMBLE_TICKS = 22; // ~0.37s grace after first contact
const RESPAWN_TICKS = 120; // ~2.0s gone before it reforms

export interface SimInput {
  left: boolean;
  right: boolean;
  jumpPressed: boolean; // edge, buffered by sim
  jumpHeld: boolean;
}

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

export class Sim {
  readonly level: ParsedLevel;

  // player state
  px = 0;
  py = 0; // top-left of AABB
  vx = 0;
  vy = 0;
  face: 1 | -1 = 1;
  grounded = false;
  dead = false;
  deadFor = 0; // seconds since death
  won = false;

  // recording + echoes
  tape: Frame[] = [];
  echoes: Echo[] = [];
  echoesLeft: number;
  t = 0; // ticks since attempt start

  // level bookkeeping
  pressedButtons = new Set<number>();
  openDoors = new Set<string>();
  /** door letters currently flipped open by a lever (toggle-mode doors) */
  toggledOn = new Set<string>();
  /** "col,row" keys of crumble slabs that have collapsed (non-solid right now) */
  brokenCrumbles = new Set<string>();
  attempt = 1;
  levelTicks = 0; // total time on this level (across attempts)

  events: FxEvent[] = [];

  private coyoteT = 0;
  private bufferT = 0;
  private springing = false; // mid spring-launch rise — suppresses the jump-cut
  private prevOpen = new Set<string>();
  // lever cells occupied last tick, so a flip fires once per step-on (rising edge)
  private leverOccupied = new Set<string>();
  // crumble timers: ticks until a touched slab breaks / until a broken slab reforms
  private crumbleBreakT = new Map<string, number>();
  private crumbleRespawnT = new Map<string, number>();
  // timed-seal doors: ticks each gate still has left open, and which timed doors
  // had all their buttons satisfied last tick — so a HELD press never re-arms
  // (only a fresh rising edge does, which a frozen echo can never produce).
  private doorCountdown = new Map<string, number>();
  private timedTriggered = new Set<string>();

  constructor(level: ParsedLevel) {
    this.level = level;
    this.echoesLeft = level.def.echoes;
    this.placeAtSpawn();
  }

  private placeAtSpawn(): void {
    this.px = this.level.spawn.x - PLAYER_W / 2;
    this.py = this.level.spawn.y - PLAYER_H;
    this.vx = 0;
    this.vy = 0;
    this.face = 1;
    this.grounded = false;
    this.dead = false;
    this.deadFor = 0;
    this.coyoteT = 0;
    this.bufferT = 0;
    this.springing = false;
  }

  playerRect(): Rect {
    return { x: this.px, y: this.py, w: PLAYER_W, h: PLAYER_H };
  }

  /** Echo's current replay frame (frozen on the last one). */
  echoFrame(e: Echo): Frame {
    const idx = Math.min(this.t, e.tape.length - 1);
    return e.tape[Math.max(0, idx)]!;
  }

  /** True once an echo has finished its tape and frozen in place. */
  echoFrozen(e: Echo): boolean {
    return this.t >= e.tape.length - 1;
  }

  /** Restart the current attempt; echoes re-arm and replay from t=0. Stateful
   *  mechanics (lever flips, crumbled slabs) reset too, so the run is
   *  deterministic and the echoes' recorded timings line up every loop. */
  resetAttempt(): void {
    this.t = 0;
    this.tape = [];
    this.attempt++;
    this.toggledOn.clear();
    this.brokenCrumbles.clear();
    this.leverOccupied.clear();
    this.crumbleBreakT.clear();
    this.crumbleRespawnT.clear();
    this.doorCountdown.clear();
    this.timedTriggered.clear();
    this.placeAtSpawn();
  }

  /** Wipe everything on this level: echoes returned, attempt restarted. */
  resetLevel(): void {
    this.echoes = [];
    this.echoesLeft = this.level.def.echoes;
    this.resetAttempt();
  }

  /** Try to spawn an echo from the current tape. Returns success. */
  spawnEcho(): boolean {
    if (this.dead || this.won) return false;
    if (this.echoesLeft <= 0 || this.tape.length < 30) {
      this.events.push({ kind: 'deny', x: this.px + PLAYER_W / 2, y: this.py });
      return false;
    }
    const id = this.echoes.length + 1;
    this.echoes.push({ tape: this.tape, hue: 285 + id * 18, id });
    this.echoesLeft--;
    this.events.push({ kind: 'echo', x: this.px + PLAYER_W / 2, y: this.py + PLAYER_H / 2 });
    this.resetAttempt();
    return true;
  }

  kill(): void {
    if (this.dead || this.won) return;
    this.dead = true;
    this.deadFor = 0;
    this.events.push({ kind: 'death', x: this.px + PLAYER_W / 2, y: this.py + PLAYER_H / 2 });
  }

  // ---- tile helpers --------------------------------------------------------

  private tileAt(col: number, row: number): string {
    if (col < 0 || row < 0 || col >= this.level.cols || row >= this.level.rows) return '#';
    return this.level.tiles[row]![col]!;
  }

  /** Is the tile at (col,row) solid RIGHT NOW? Handles closed doors, spring pads
   *  (always solid) and crumble slabs (solid until they collapse). */
  private solidAt(col: number, row: number): boolean {
    const ch = this.tileAt(col, row);
    if (ch === '#' || ch === '*') return true;
    if (ch >= 'A' && ch <= 'D') return !this.openDoors.has(ch);
    if (ch === 'x') return !this.brokenCrumbles.has(`${col},${row}`);
    return false;
  }

  /** Horizontal solidity test for the rect; returns true if blocked. */
  private blockedH(r: Rect): boolean {
    const c0 = Math.floor(r.x / TILE);
    const c1 = Math.floor((r.x + r.w - 0.01) / TILE);
    const r0 = Math.floor(r.y / TILE);
    const r1 = Math.floor((r.y + r.h - 0.01) / TILE);
    for (let row = r0; row <= r1; row++) {
      for (let col = c0; col <= c1; col++) {
        if (this.solidAt(col, row)) return true;
      }
    }
    return false;
  }

  // ---- main tick -----------------------------------------------------------

  tick(input: SimInput): void {
    this.levelTicks++;
    if (this.won) return;

    if (this.dead) {
      this.deadFor += TICK;
      if (this.deadFor > 0.55) this.resetAttempt();
      return;
    }

    // mechanic states first (echo positions at THIS tick decide everything):
    // levers flip toggle-doors, slabs crumble, then buttons/doors resolve.
    this.updateLevers();
    this.updateCrumbles();
    this.updateButtonsAndDoors();

    // --- horizontal movement
    const dir = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    const accel = this.grounded ? GROUND_ACCEL : AIR_ACCEL;
    const target = dir * MOVE_SPEED;
    if (this.vx < target) this.vx = Math.min(target, this.vx + accel * TICK);
    else if (this.vx > target) this.vx = Math.max(target, this.vx - accel * TICK);
    if (dir !== 0) this.face = dir as 1 | -1;

    this.px += this.vx * TICK;
    if (this.blockedH(this.playerRect())) {
      // resolve by stepping back to tile boundary
      if (this.vx > 0) {
        const col = Math.floor((this.px + PLAYER_W - 0.01) / TILE);
        this.px = col * TILE - PLAYER_W - 0.01;
      } else if (this.vx < 0) {
        const col = Math.floor(this.px / TILE);
        this.px = (col + 1) * TILE + 0.01;
      }
      this.vx = 0;
    }

    // --- vertical movement
    this.vy = Math.min(this.vy + GRAVITY * TICK, MAX_FALL);
    const fallSpeed = this.vy;
    const prevBottom = this.py + PLAYER_H;
    this.py += this.vy * TICK;

    const wasGrounded = this.grounded;
    this.grounded = false;

    if (this.vy >= 0) {
      // falling: solids, one-way tiles, spring pads, frozen-or-replaying echo heads
      const bottom = this.py + PLAYER_H;
      const row = Math.floor((bottom - 0.01) / TILE);
      const c0 = Math.floor(this.px / TILE);
      const c1 = Math.floor((this.px + PLAYER_W - 0.01) / TILE);
      for (let col = c0; col <= c1; col++) {
        const ch = this.tileAt(col, row);
        const tileTop = row * TILE;
        const solid = this.solidAt(col, row);
        const oneWay = ch === '-' && prevBottom <= tileTop + 1;
        if (solid || oneWay) {
          this.py = tileTop - PLAYER_H;
          if (ch === '*') {
            // spring: bounce instead of settle (stay airborne, full height)
            this.vy = -SPRING_VEL;
            this.springing = true;
            this.events.push({ kind: 'spring', x: col * TILE + TILE / 2, y: tileTop });
          } else {
            this.vy = 0;
            this.grounded = true;
            this.springing = false;
          }
          break;
        }
      }
      // echo heads as one-way platforms
      if (!this.grounded) {
        for (const e of this.echoes) {
          const f = this.echoFrame(e);
          const headTop = f.y;
          if (
            prevBottom <= headTop + 4 &&
            this.py + PLAYER_H >= headTop &&
            this.px + PLAYER_W > f.x + 2 &&
            this.px < f.x + PLAYER_W - 2
          ) {
            this.py = headTop - PLAYER_H;
            this.vy = 0;
            this.grounded = true;
            this.springing = false;
            break;
          }
        }
      }
    } else {
      // rising: head bump on solids
      const top = this.py;
      const row = Math.floor(top / TILE);
      const c0 = Math.floor(this.px / TILE);
      const c1 = Math.floor((this.px + PLAYER_W - 0.01) / TILE);
      for (let col = c0; col <= c1; col++) {
        if (this.solidAt(col, row)) {
          this.py = (row + 1) * TILE + 0.01;
          this.vy = 0;
          break;
        }
      }
    }

    if (this.grounded && !wasGrounded && fallSpeed > 320) {
      this.events.push({ kind: 'land', x: this.px + PLAYER_W / 2, y: this.py + PLAYER_H });
    }

    // --- jumping (coyote + buffer)
    this.coyoteT = this.grounded ? COYOTE : Math.max(0, this.coyoteT - TICK);
    this.bufferT = input.jumpPressed ? JUMP_BUFFER : Math.max(0, this.bufferT - TICK);
    if (this.bufferT > 0 && this.coyoteT > 0) {
      this.vy = -JUMP_VEL;
      this.grounded = false;
      this.coyoteT = 0;
      this.bufferT = 0;
    }
    // variable jump height: cut velocity when jump released (springs are exempt
    // for the whole rise, so a spring reaches full height regardless of jump)
    if (!input.jumpHeld && !this.springing && this.vy < -260) this.vy = -260;

    // --- hazards
    const pr = this.playerRect();
    const r0 = Math.floor(pr.y / TILE);
    const r1 = Math.floor((pr.y + pr.h - 0.01) / TILE);
    const c0 = Math.floor(pr.x / TILE);
    const c1 = Math.floor((pr.x + pr.w - 0.01) / TILE);
    outer: for (let row = r0; row <= r1; row++) {
      for (let col = c0; col <= c1; col++) {
        if (this.tileAt(col, row) === '^') {
          const spike: Rect = { x: col * TILE + 4, y: row * TILE + TILE * 0.35, w: TILE - 8, h: TILE * 0.65 };
          if (overlaps(pr, spike)) {
            this.kill();
            break outer;
          }
        }
      }
    }
    // squished by a closing door
    if (!this.dead && this.blockedH(pr)) this.kill();
    // fell out of the world
    if (!this.dead && this.py > this.level.rows * TILE + 200) this.kill();

    if (this.dead) return;

    // --- exit
    const ex: Rect = {
      x: this.level.exit.col * TILE + 3,
      y: this.level.exit.row * TILE - 6,
      w: TILE - 6,
      h: TILE + 6,
    };
    const sealOpen = !this.level.def.seal || this.openDoors.has(this.level.def.seal);
    if (overlaps(pr, ex) && sealOpen) {
      this.won = true;
      this.events.push({ kind: 'win', x: ex.x + ex.w / 2, y: ex.y + ex.h / 2 });
      return;
    }

    // --- record this tick
    if (this.tape.length < MAX_TAPE) {
      this.tape.push({ x: this.px, y: this.py, face: this.face, grounded: this.grounded });
    }
    this.t++;
  }

  /** Player + every echo as collision rects — the bodies that press pads, throw
   *  levers and weigh on crumble slabs. */
  private bodyRects(): Rect[] {
    const out: Rect[] = [this.playerRect()];
    for (const e of this.echoes) {
      const f = this.echoFrame(e);
      out.push({ x: f.x, y: f.y, w: PLAYER_W, h: PLAYER_H });
    }
    return out;
  }

  private updateButtonsAndDoors(): void {
    this.pressedButtons.clear();
    const bodies = this.bodyRects();
    for (const b of this.level.buttons) {
      const pad: Rect = { x: b.col * TILE + 3, y: b.row * TILE + TILE - 16, w: TILE - 6, h: 18 };
      if (bodies.some((r) => overlaps(r, pad))) this.pressedButtons.add(b.id);
    }
    this.openDoors.clear();
    for (const d of this.level.doors) {
      let open: boolean;
      if (d.mode === 'toggle') open = this.toggledOn.has(d.letter);
      else if (d.mode === 'timed') open = this.resolveTimedDoor(d);
      else open = d.needs.length > 0 && d.needs.every((id) => this.pressedButtons.has(id));
      if (open) {
        this.openDoors.add(d.letter);
        if (!this.prevOpen.has(d.letter)) {
          const cell = d.cells[0]!;
          this.events.push({ kind: 'door', x: cell.col * TILE + TILE / 2, y: cell.row * TILE + TILE });
        }
      }
    }
    this.prevOpen = new Set(this.openDoors);
  }

  /** A momentary TIMED seal. A FRESH (rising-edge) press of all its buttons arms
   *  it for `windowTicks`; it then ticks down and auto-shuts even while still
   *  held. Returns whether it is open THIS tick. A held press (frozen echo) is
   *  not a rising edge, so a parked staircase of past selves can never re-arm it
   *  — this is what structurally kills the stack-to-the-exit cheese. */
  private resolveTimedDoor(d: DoorDef): boolean {
    const satisfied = d.needs.length > 0 && d.needs.every((id) => this.pressedButtons.has(id));
    const fresh = satisfied && !this.timedTriggered.has(d.letter);
    if (fresh) this.doorCountdown.set(d.letter, d.windowTicks ?? 42);
    if (satisfied) this.timedTriggered.add(d.letter);
    else this.timedTriggered.delete(d.letter);
    const left = this.doorCountdown.get(d.letter) ?? 0;
    if (left <= 0) {
      this.doorCountdown.delete(d.letter);
      return false;
    }
    this.doorCountdown.set(d.letter, left - 1);
    return true;
  }

  /** Levers flip their door's toggle state once per fresh step-on (rising edge). */
  private updateLevers(): void {
    if (this.level.levers.length === 0) return;
    const bodies = this.bodyRects();
    for (const lv of this.level.levers) {
      const key = `${lv.col},${lv.row}`;
      const pad: Rect = { x: lv.col * TILE + 3, y: lv.row * TILE + TILE - 16, w: TILE - 6, h: 18 };
      const on = bodies.some((r) => overlaps(r, pad));
      if (on && !this.leverOccupied.has(key)) {
        if (this.toggledOn.has(lv.letter)) this.toggledOn.delete(lv.letter);
        else this.toggledOn.add(lv.letter);
        this.events.push({ kind: 'lever', x: lv.col * TILE + TILE / 2, y: lv.row * TILE + TILE });
      }
      if (on) this.leverOccupied.add(key);
      else this.leverOccupied.delete(key);
    }
  }

  /** Crumble slabs: start a countdown when first stood on, break when it runs
   *  out, then reform after a delay (never popping a body up through it). */
  private updateCrumbles(): void {
    if (this.level.crumbles.length === 0) return;
    const bodies = this.bodyRects();
    for (const cell of this.level.crumbles) {
      const key = `${cell.col},${cell.row}`;
      const x = cell.col * TILE;
      const y = cell.row * TILE;
      if (this.brokenCrumbles.has(key)) {
        const full: Rect = { x, y, w: TILE, h: TILE };
        const rt = (this.crumbleRespawnT.get(key) ?? 0) - 1;
        if (rt <= 0 && !bodies.some((r) => overlaps(r, full))) {
          this.brokenCrumbles.delete(key);
          this.crumbleRespawnT.delete(key);
        } else {
          this.crumbleRespawnT.set(key, rt);
        }
        continue;
      }
      const running = this.crumbleBreakT.get(key);
      if (running !== undefined) {
        const t = running - 1;
        if (t <= 0) {
          this.brokenCrumbles.add(key);
          this.crumbleRespawnT.set(key, RESPAWN_TICKS);
          this.crumbleBreakT.delete(key);
          this.events.push({ kind: 'crumble', x: x + TILE / 2, y: y + TILE / 2 });
        } else {
          this.crumbleBreakT.set(key, t);
        }
      } else {
        const topStrip: Rect = { x: x + 1, y: y - 2, w: TILE - 2, h: 9 };
        if (bodies.some((r) => overlaps(r, topStrip))) this.crumbleBreakT.set(key, CRUMBLE_TICKS);
      }
    }
  }

  /** 0 (intact / untouched) → 1 (about to collapse); for the renderer's shake. */
  crumbleBreakFrac(col: number, row: number): number {
    const t = this.crumbleBreakT.get(`${col},${row}`);
    return t === undefined ? 0 : 1 - t / CRUMBLE_TICKS;
  }

  /** A timed seal's remaining window as 1 (just armed) → 0 (shut), for the
   *  renderer's countdown bar / exit pulse. 0 for non-timed or closed doors. */
  timedDoorFrac(letter: string): number {
    const left = this.doorCountdown.get(letter);
    if (left === undefined || left <= 0) return 0;
    const d = this.level.doors.find((x) => x.letter === letter);
    const win = d?.windowTicks ?? 42;
    return Math.max(0, Math.min(1, left / win));
  }

  /** Whether this level's exit is sealed and currently armed (open). For the
   *  renderer to pulse the exit when it is winnable. */
  get exitArmed(): boolean {
    const seal = this.level.def.seal;
    return !seal || this.openDoors.has(seal);
  }
}
